import { useState } from 'react';
import { BsCalendarDay } from 'react-icons/bs';
import { FiClock, FiEdit2, FiFilter, FiGrid, FiList, FiMapPin, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import { HiOutlineOfficeBuilding, HiSparkles } from 'react-icons/hi';
import { useLeaders } from '../../contexts/LeaderContext';
import { useMinistries } from '../../contexts/MinistryContext';
import MinistryForm from './forms/MinistryForm';
import ConfirmDialog from './ui/ConfirmDialog';
import EmptyState from './ui/EmptyState';
import Modal from './ui/Modal';
import StatusBadge from './ui/StatusBadge';

const MinistriesManager = () => {
  // Mock data for demonstration
  const {ministries, loading, setLoading, setMinistries, error} = useMinistries();
  const {leaders} = useLeaders();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [currentMinistry, setCurrentMinistry] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  const filteredMinistries = ministries
    .filter(ministry => {
      const matchesSearch = ministry.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterStatus === 'all' || ministry.status === filterStatus;
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'date':
          return a.updatedAT.localeCompare(b.updatedAT);
        default:
          return 0;
      }
    });

  const renderArrayField = (array, options = {}) => {
    if (!array || !Array.isArray(array) || array.length === 0) {
      return (
        <span className="text-slate-400 text-sm">
          {options.emptyText || 'Not assigned'}
        </span>
      );
    }

    return (
      <div className="flex flex-wrap gap-1.5">
        {array.map((item, index) => (
          <span 
            key={index}
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              options.variant === 'primary' 
                ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-sm'
                : 'bg-slate-100 text-slate-700'
            }`}
          >
            {typeof item === 'object' 
              ? item.name || item.title || item.fullName || `${item.firstName} ${item.lastName}` || 'Unnamed'
              : String(item)
            }
          </span>
        ))}
      </div>
    );
  };

  const handleDeleteClick = (ministry) => {
    setCurrentMinistry(ministry);
    setIsDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!currentMinistry) return;
    
    setProcessing(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setMinistries(ministries.filter(item => item.id !== currentMinistry.id));
      setIsDeleteConfirmOpen(false);
      setCurrentMinistry(null);
    } catch (error) {
      console.error('Failed to delete ministry:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = (ministry) => {
    setCurrentMinistry(ministry);
    setIsModalOpen(true);
  };

  const handleSubmit = async (formData) => {
    setProcessing(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      let updatedMinistries;
      if (currentMinistry) {
        updatedMinistries = ministries.map(m => 
          m.id === currentMinistry.id ? { ...m, ...formData } : m
        );
      } else {
        const newMinistry = {
          id: Date.now(),
          ...formData,
          status: 'active'
        };
        updatedMinistries = [...ministries, newMinistry];
      }
      setMinistries(updatedMinistries);
      setIsModalOpen(false);
      setCurrentMinistry(null);
    } catch (error) {
      console.error('Failed to save ministry:', error);
    } finally {
      setProcessing(false);
    }
  };

  const GridCard = ({ ministry }) => (
    <div className="group relative bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-200/60 overflow-hidden hover:-translate-y-1">
      {/* Cover Image */}
      <div className="relative h-32 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 overflow-hidden">
        {ministry.cover_image && (
          <img 
            src={ministry.cover_image} 
            alt={ministry.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        <div className="absolute top-3 right-3">
          <StatusBadge status={ministry.status} />
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start mb-4">
          <div className="relative">
            <img 
              className="h-12 w-12 rounded-xl object-cover border-2 border-white shadow-lg ring-2 ring-slate-100" 
              src={ministry.logo_image || '/api/placeholder/48/48'} 
              alt={ministry.name}
              onError={(e) => {
                e.target.src = '/api/placeholder/48/48';
              }}
            />
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white" />
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-slate-900 truncate">
              {ministry.name}
            </h3>
            <p className="text-sm text-slate-500 line-clamp-2">
              {ministry.description}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-slate-50 rounded-lg p-2.5">
            <div className="flex items-center text-sm text-slate-600">
              <BsCalendarDay className="w-4 h-4 mr-1.5 text-emerald-500" />
              <span className="font-medium">{ministry.meeting_day}</span>
            </div>
            <div className="text-xs text-slate-500">Meeting Day</div>
          </div>
        </div>

        {/* Meeting Info */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm text-slate-600">
            <FiClock className="w-4 h-4 mr-2 text-slate-400" />
            <span>{ministry.meeting_time || 'No time set'}</span>
          </div>
          <div className="flex items-center text-sm text-slate-600">
            <FiMapPin className="w-4 h-4 mr-2 text-slate-400" />
            <span className="truncate">{ministry.location || 'All Campuses'}</span>
          </div>
        </div>

        {/* Leader */}
        <div className="mb-4">
          <div className="text-xs text-slate-500 mb-1">Leader</div>
          {renderArrayField(
            leaders.filter(leader => 
              leader.leaderType.includes("ministry") && 
              leader.ministryId === ministry.id
            ),
            { emptyText: 'No leaders', variant: 'primary' }
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => handleEdit(ministry)}
            className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
          >
            <FiEdit2 className="w-4 h-4 mr-1.5" />
            Edit
          </button>
          <button
            onClick={() => handleDeleteClick(ministry)}
            className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  const ListItem = ({ ministry }) => (
    <div className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-slate-200/60 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center flex-1 min-w-0">
          <div className="relative">
            <img 
              className="h-14 w-14 rounded-xl object-cover border-2 border-white shadow-sm ring-2 ring-slate-100" 
              src={ministry.logo_image || '/api/placeholder/56/56'} 
              alt={ministry.name}
            />
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white" />
          </div>
          
          <div className="ml-4 flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-lg font-semibold text-slate-900 truncate">
                {ministry.name}
              </h3>
              <StatusBadge status={ministry.status} />
            </div>
            <p className="text-sm text-slate-500 mb-2 line-clamp-1">
              {ministry.description}
            </p>
            
            <div className="flex items-center gap-6 text-sm text-slate-600">
              <div className="flex items-center">
                <BsCalendarDay className="w-4 h-4 mr-1.5 text-emerald-500" />
                <span>{ministry.meeting_day} • {ministry.meeting_time}</span>
              </div>
              <div className="flex items-center">
                <FiMapPin className="w-4 h-4 mr-1.5 text-slate-400" />
                <span className="truncate">{ministry.location}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={() => handleEdit(ministry)}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
          >
            <FiEdit2 className="w-4 h-4 mr-1.5" />
            Edit
          </button>
          <button
            onClick={() => handleDeleteClick(ministry)}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-white/20 p-8 mb-8 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/5 via-purple-500/5 to-pink-500/5" />
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-500/20 to-purple-600/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blue-500/20 to-cyan-500/20 rounded-full blur-2xl" />
          
          <div className="relative">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <HiOutlineOfficeBuilding className="w-7 h-7 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                    <HiSparkles className="w-3 h-3 text-white" />
                  </div>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-slate-900">
                    Ministries Management
                  </h1>
                  <p className="text-slate-600 mt-1">
                    Organize and manage all church ministries in one place
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="bg-slate-100 rounded-xl p-1 flex items-center gap-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-all ${
                      viewMode === 'grid'
                        ? 'bg-white shadow-sm text-indigo-600'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <FiGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-all ${
                      viewMode === 'list'
                        ? 'bg-white shadow-sm text-indigo-600'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <FiList className="w-4 h-4" />
                  </button>
                </div>
                
                <button
                  onClick={() => {
                    setCurrentMinistry(null);
                    setIsModalOpen(true);
                  }}
                  className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  <FiPlus className="w-4 h-4 mr-2" />
                  Add Ministry
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search ministries..."
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-slate-900"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filters */}
            <div className="flex gap-3">
              <div className="relative">
                <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="appearance-none pl-10 pr-8 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-slate-900 min-w-[120px]"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-slate-900 min-w-[120px]"
              >
                <option value="name">Sort by Name</option>
                <option value="day">Sort by Date</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Ministries</p>
                <p className="text-3xl font-bold text-slate-900">{ministries.length}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <HiOutlineOfficeBuilding className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Active Ministries</p>
                <p className="text-3xl font-bold text-slate-900">
                  {ministries.filter(m => m.status === 'active').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <div className="w-3 h-3 bg-white rounded-full" />
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-medium text-red-800">Error loading ministries</h3>
                <p className="text-sm text-red-700 mt-1">
                  {error.message || 'Something went wrong. Please try again.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Ministries List */}
        {loading ? (
          <div className="space-y-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-6">
                <div className="animate-pulse">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-slate-200 rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-slate-200 rounded w-1/4" />
                      <div className="h-3 bg-slate-200 rounded w-3/4" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredMinistries.length === 0 ? (
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20">
            <EmptyState />
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
            {filteredMinistries.map((ministry) => (
              viewMode === 'grid' ? (
                <GridCard key={ministry.id} ministry={ministry} />
              ) : (
                <ListItem key={ministry.id} ministry={ministry} />
              )
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setCurrentMinistry(null);
          }}
          title={
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                <HiOutlineOfficeBuilding className="w-4 h-4 text-white" />
              </div>
              {currentMinistry ? 'Edit Ministry' : 'Create New Ministry'}
            </div>
          }
          size="lg"
        >
          <MinistryForm
            initialValues={currentMinistry || {
              name: '',
              leader: '',
              description: '',
              cover_image: '',
              logo_image: '',
              meeting_day: '',
              meeting_time: '',
              location: ''
            }}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsModalOpen(false);
              setCurrentMinistry(null);
            }}
            isSubmitting={processing}
          />
        </Modal>

        {/* Delete Confirmation */}
        <ConfirmDialog
          isOpen={isDeleteConfirmOpen}
          onClose={() => {
            setIsDeleteConfirmOpen(false);
            setCurrentMinistry(null);
          }}
          onConfirm={handleDelete}
          title="Delete Ministry"
          message={
            <div className="space-y-4">
              <p>Are you sure you want to delete this ministry? This action cannot be undone.</p>
              {currentMinistry && (
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center">
                    <img 
                      className="w-10 h-10 rounded-lg object-cover mr-3" 
                      src={currentMinistry?.logo_image || '/api/placeholder/40/40'} 
                      alt={currentMinistry?.name}
                    />
                    <div>
                      <p className="font-medium text-slate-900">
                        {currentMinistry?.name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {currentMinistry?.members} members
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          }
          processing={processing}
        />
      </div>
    </div>
  );
};

export default MinistriesManager;